import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { createFormHelper } from "lib/Form";
import { ASTERISK_PLACEHOLDER } from "modules/DealerOverviewDashboard/components";

import type { Dealer } from "models";
import type { DealerAdminFormPayload } from "modules/DealerDetails";

const FormHelper = createFormHelper<DealerAdminFormPayload>();

const MAX_RETENTION_IN_YEARS = 20;
const retentionDropdownOptions = Array.from({ length: MAX_RETENTION_IN_YEARS }, (_, idx) => ({ key: idx + 1, value: idx + 1, text: `${idx + 1}` }));

type ConfigurationProps = {
  tabId: string;
  dealer: Dealer;
};

export const Configuration = ({ tabId, dealer }: ConfigurationProps) => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();
  const [isPonEnabled, ponUsername, ponPassword] = watch(["is_pon_enabled", "pon_username", "pon_password"]);

  const isPonUsernameInvalid = isPonEnabled && !ponUsername;
  const isPonPasswordInvalid = isPonEnabled && !dealer.has_pon_password && !ponPassword;

  return (
    <Element name={tabId} className="dealer-details-section">
      <div className="section-title">
        <div>
          <Icon className="wrench" />
        </div>
        <span className="section-title-text">{t("v8_configuration", "Configuration")}</span>
      </div>

      <Form.Group className="dealer-details-columns">
        <Form.Field width={8}>
          <div className="dealer-details-subheading">{t("v8_configuration", "Configuration")}</div>
          <Form.Field className="dealer-details-column-field">
            <FormHelper.Field.Checkbox name="can_add_users" label={t("v8_can_add_users", "Can add users")} />
          </Form.Field>
          <Form.Field className="dealer-details-column-field">
            <FormHelper.Field.Checkbox name="dbb_enabled" label={t("v8_dbb", "DBB")} />
          </Form.Field>
          <Form.Field className="dealer-details-column-field">
            <FormHelper.Field.Checkbox name="custom_domain_enabled" label={t("v8_custom_domain", "Custom Domain")} />
          </Form.Field>
          <Form.Field className="dealer-details-column-field">
            <FormHelper.Field.Checkbox name="enable_car_ready" label={t("v8_car_return_time", "Car Return Time")} />
          </Form.Field>
          <Form.Field className="dealer-details-column-field">
            <FormHelper.Field.Checkbox name="advised_critical_history_enabled" label={t("v8_advised_critical_history", "Advised and critical history")} />
          </Form.Field>
        </Form.Field>
        <Form.Field width={8}>
          <div className="dealer-details-subheading">{t("v8_gdpr_avg", "GDPR - AVG")}</div>
          <Form.Field className="dealer-details-column-field">
            <label>{t("v8_customer_data_retention_period_in_years", "Customer data retention period (in years)")}</label>
            <FormHelper.Field.Dropdown name="customer_data_retention_in_years" options={retentionDropdownOptions} />
          </Form.Field>
        </Form.Field>
      </Form.Group>
      <Divider />
      <Form.Group className="dealer-details-columns">
        <Form.Field width={8}>
          <div className="dealer-details-subheading">{t("v8_pon_configuration", "PON configuration")}</div>
          <Form.Field className="dealer-details-column-field">
            <FormHelper.Field.Checkbox name="is_pon_enabled" label={t("v8_enable_pon", "Enable PON")} />
          </Form.Field>
          <Form.Field className="dealer-details-column-field" error={isPonUsernameInvalid}>
            <label>{t("v8_username", "Username")}</label>
            <FormHelper.Field.Input name="pon_username" preventAutofill />
          </Form.Field>
          <Form.Field className="dealer-details-column-field" error={isPonPasswordInvalid}>
            <label>{t("v8_password", "Password")}</label>
            <FormHelper.Field.Input
              name="pon_password"
              preventAutofill
              inputProps={{ type: "password", placeholder: dealer.has_pon_password ? ASTERISK_PLACEHOLDER : "" }}
            />
          </Form.Field>
        </Form.Field>
        <Form.Field width={8}>
          <div className="dealer-details-subheading">{t("v8_lef_configuration", "LEF configuration")}</div>
          <Form.Field className="dealer-details-column-field dealer-details-column-placeholder" />
          <Form.Field className="dealer-details-column-field">
            <label>{t("v8_username", "Username")}</label>
            <FormHelper.Field.Input name="lef_username" preventAutofill />
          </Form.Field>
          <Form.Field className="dealer-details-column-field">
            <label>{t("v8_password", "Password")}</label>
            <FormHelper.Field.Input
              name="lef_password"
              preventAutofill
              inputProps={{ type: "password", placeholder: dealer.has_lef_password ? ASTERISK_PLACEHOLDER : "" }}
            />
          </Form.Field>
        </Form.Field>
      </Form.Group>
    </Element>
  );
};
